import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, RotateCcw, Volume2, VolumeX, Music, Repeat, X } from 'lucide-react';
import { usePubSub } from '../../../config/sdkConfig';
import { isIOSAudioUnlocked } from '../../../utils/iosAudioUnlock';

export default function AudioPlayerBar({ audioData, onStop, isHost = true }) {
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const publishRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState((audioData?.playback?.volume ?? 80) / 100);
  const [muted, setMuted] = useState(false);
  const [isLooping, setIsLooping] = useState(audioData?.playback?.loop || false);
  const [isHovered, setIsHovered] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [localTime, setLocalTime] = useState(0);
  const [needsUserGesture, setNeedsUserGesture] = useState(false);

  const fileUrl = audioData?.file_url;
  const name = audioData?.name || 'Audio';
  const autoplay = audioData?.playback?.autoplay !== false;

  // PubSub for syncing audio controls between host and participants
  const { publish: publishAudioControl } = usePubSub("AUDIO_CONTROL", {
    onMessageReceived: (data) => {
      // Only participants react to control messages from host
      if (isHost) return;
      // Ignore stale persisted messages from previous sessions
      if (data.isPersisted) return;
      try {
        const ctrl = JSON.parse(data.message);
        const audio = audioRef.current;
        if (!audio) return;

        if (ctrl.action === 'play') {
          if (ctrl.currentTime !== undefined) audio.currentTime = ctrl.currentTime;
          audio.play().then(() => {
            setNeedsUserGesture(false);
          }).catch(() => {
            // iOS may need a moment after AudioContext unlock — retry once
            if (isIOSAudioUnlocked()) {
              setTimeout(() => {
                audio.play().then(() => setNeedsUserGesture(false)).catch(() => setNeedsUserGesture(true));
              }, 300);
            } else {
              setNeedsUserGesture(true);
            }
          });
        } else if (ctrl.action === 'pause') {
          audio.pause();
          if (ctrl.currentTime !== undefined) audio.currentTime = ctrl.currentTime;
        } else if (ctrl.action === 'stop') {
          audio.pause();
          audio.currentTime = 0;
        } else if (ctrl.action === 'volume') {
          // Sync volume from host to participants
          if (ctrl.volume !== undefined) {
            audio.volume = ctrl.volume;
          }
        }
      } catch (e) {
        console.error('[AudioPlayerBar] Error handling audio control:', e);
      }
    }
  });

  // Keep publish ref current to avoid stale closures
  useEffect(() => {
    publishRef.current = publishAudioControl;
  }, [publishAudioControl]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // --- Host actions with PubSub sync ---

  const handlePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.play().then(() => {
      setNeedsUserGesture(false);
    }).catch(() => {
      setNeedsUserGesture(true);
    });
    if (isHost && publishRef.current) {
      publishRef.current(JSON.stringify({
        action: 'play',
        currentTime: audio.currentTime
      }), { persist: true });
    }
  }, [isHost]);

  const handlePause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    if (isHost && publishRef.current) {
      publishRef.current(JSON.stringify({
        action: 'pause',
        currentTime: audio.currentTime
      }), { persist: true });
    }
  }, [isHost]);

  const togglePlay = useCallback(() => {
    if (playing) handlePause();
    else handlePlay();
  }, [playing, handlePlay, handlePause]);

  const handleStop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setPlaying(false);
    if (isHost && publishRef.current) {
      publishRef.current(JSON.stringify({ action: 'stop' }), { persist: true });
    }
    onStop?.();
  }, [onStop, isHost]);

  const handleRestart = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    audio.play().then(() => setNeedsUserGesture(false)).catch(() => {});
    if (isHost && publishRef.current) {
      publishRef.current(JSON.stringify({
        action: 'play',
        currentTime: 0
      }), { persist: true });
    }
  }, [isHost]);

  const handleLoopToggle = useCallback(() => {
    setIsLooping(prev => !prev);
  }, []);

  const handleMuteToggle = useCallback(() => {
    setMuted(prev => !prev);
  }, []);

  // --- Progress bar ---

  const handleProgressClick = useCallback((e) => {
    if (!isHost || !progressRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const newTime = pct * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
    setLocalTime(newTime);
    if (publishRef.current) {
      publishRef.current(JSON.stringify({
        action: playing ? 'play' : 'pause',
        currentTime: newTime
      }), { persist: true });
    }
  }, [duration, isHost, playing]);

  const handleProgressDrag = useCallback((e) => {
    if (!isDragging || !progressRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setLocalTime(pct * duration);
  }, [isDragging, duration]);

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    if (audioRef.current) {
      audioRef.current.currentTime = localTime;
    }
    if (isHost && publishRef.current) {
      publishRef.current(JSON.stringify({
        action: playing ? 'play' : 'pause',
        currentTime: localTime
      }), { persist: true });
    }
  }, [isDragging, localTime, isHost, playing]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleProgressDrag);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleProgressDrag);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleProgressDrag, handleDragEnd]);

  // Sync localTime with currentTime when not dragging
  useEffect(() => {
    if (!isDragging) setLocalTime(currentTime);
  }, [currentTime, isDragging]);

  // --- Audio element event listeners ---

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onDurationChange = () => setDuration(audio.duration);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      if (!isLooping && isHost) handleStop();
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, [isLooping, isHost]); // eslint-disable-line react-hooks/exhaustive-deps

  // Volume — sync to participants when host changes volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = muted ? 0 : volume;
    }
    // Broadcast volume change to participants
    if (isHost && publishRef.current) {
      publishRef.current(JSON.stringify({
        action: 'volume',
        volume: muted ? 0 : volume
      }));
    }
  }, [volume, muted, isHost]);

  // Autoplay on mount — works for both host and participants
  useEffect(() => {
    if (autoplay && audioRef.current && fileUrl) {
      audioRef.current.play().then(() => {
        setNeedsUserGesture(false);
      }).catch(() => {
        // iOS Safari blocks autoplay — retry if audio was unlocked during join
        if (isIOSAudioUnlocked()) {
          setTimeout(() => {
            audioRef.current?.play().then(() => setNeedsUserGesture(false)).catch(() => setNeedsUserGesture(true));
          }, 300);
        } else {
          setNeedsUserGesture(true);
        }
      });
      if (isHost) {
        // Small delay to ensure PubSub is ready
        setTimeout(() => {
          publishRef.current?.(JSON.stringify({
            action: 'play',
            currentTime: 0
          }), { persist: true });
        }, 500);
      }
    }
  }, [fileUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!fileUrl) return null;

  const progressPercent = duration > 0 ? (localTime / duration) * 100 : 0;

  // ========== Participant view: listen-only ==========
  if (!isHost) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        className="fixed bottom-20 left-0 right-0 mx-auto z-50 w-[90vw] max-w-xs sm:max-w-sm px-2"
      >
        <audio ref={audioRef} src={fileUrl} loop={isLooping} preload="auto" playsInline />
        {needsUserGesture ? (
          <button
            onClick={() => {
              const audio = audioRef.current;
              if (!audio) return;
              audio.play().then(() => setNeedsUserGesture(false)).catch(() => {});
            }}
            className="w-full bg-gradient-to-r from-green-900/90 to-emerald-900/90 backdrop-blur-md rounded-full px-3 sm:px-4 py-2.5 shadow-lg flex items-center gap-2 sm:gap-3 border border-green-500/30 active:opacity-80 transition-opacity"
          >
            <Play className="w-4 h-4 text-green-400 flex-shrink-0" />
            <span className="text-xs text-white/90 truncate min-w-0">Tap to play: {name}</span>
          </button>
        ) : (
          <div className="bg-gradient-to-r from-green-900/80 to-emerald-900/80 backdrop-blur-md rounded-full px-3 sm:px-4 py-2 shadow-lg flex items-center gap-2 sm:gap-3 border border-green-500/30">
            <Music className="w-4 h-4 text-green-400 animate-pulse flex-shrink-0" />
            <span className="text-xs text-white/80 truncate min-w-0">{name}</span>
            <div className="flex-1 min-w-[40px] h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-400 rounded-full transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-[10px] text-white/70 font-mono flex-shrink-0">
              {formatTime(currentTime)}
            </span>
          </div>
        )}
      </motion.div>
    );
  }

  // ========== Host view: full controls ==========
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: isHovered ? 1 : 0.85 }}
      exit={{ opacity: 0, y: 10 }}
      className="fixed bottom-20 left-0 right-0 mx-auto z-50 w-[calc(100vw-1rem)] max-w-md sm:max-w-lg px-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setShowVolumeSlider(false); }}
    >
      <audio ref={audioRef} src={fileUrl} loop={isLooping} preload="auto" playsInline />

      <div className="bg-gradient-to-r from-green-900/80 to-emerald-900/80 backdrop-blur-md rounded-full pl-3 sm:pl-4 pr-1.5 py-1.5 shadow-lg flex items-center gap-1.5 sm:gap-2 border border-green-500/30">
        {/* Audio Icon + Name */}
        <div className="flex items-center gap-1.5 sm:gap-2 pr-2 border-r border-white/20 flex-shrink-0">
          <Music className="w-4 h-4 text-green-400 flex-shrink-0" />
          <span className="text-xs text-white/80 max-w-[60px] sm:max-w-[100px] truncate">{name}</span>
        </div>

        {/* Progress Bar */}
        <div
          ref={progressRef}
          className="relative flex-1 min-w-[40px] h-1.5 bg-white/20 rounded-full cursor-pointer group mx-1 sm:mx-2"
          onClick={handleProgressClick}
          onMouseDown={() => setIsDragging(true)}
        >
          <div
            className="absolute inset-y-0 left-0 bg-green-400 rounded-full"
            style={{ width: `${progressPercent}%` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-green-400 rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ left: `calc(${progressPercent}% - 6px)` }}
          />
        </div>

        {/* Time */}
        <span className="text-[10px] text-white/70 min-w-[50px] sm:min-w-[60px] text-center font-mono flex-shrink-0">
          {formatTime(localTime)} / {formatTime(duration)}
        </span>

        <div className="w-px h-5 bg-white/20 mx-0.5 sm:mx-1 flex-shrink-0" />

        {/* Play/Pause */}
        <button
          onClick={togglePlay}
          className="p-1 sm:p-1.5 hover:bg-white/10 rounded-full transition-colors flex-shrink-0"
        >
          {playing ? (
            <Pause className="w-4 h-4 text-white" />
          ) : (
            <Play className="w-4 h-4 text-white ml-0.5" />
          )}
        </button>

        {/* Restart */}
        <button
          onClick={handleRestart}
          className="p-1 sm:p-1.5 hover:bg-white/10 rounded-full transition-colors flex-shrink-0"
          title="Restart"
        >
          <RotateCcw className="w-3.5 h-3.5 text-white/70" />
        </button>

        {/* Loop */}
        <button
          onClick={handleLoopToggle}
          className={`p-1 sm:p-1.5 rounded-full transition-colors flex-shrink-0 ${isLooping ? 'bg-green-500/30 text-green-400' : 'hover:bg-white/10 text-white/70'}`}
          title={isLooping ? 'Loop On' : 'Loop Off'}
        >
          <Repeat className="w-3.5 h-3.5" />
        </button>

        {/* Volume — slider opens as a popover above the bar to avoid pushing the end button out */}
        <div
          className="relative flex items-center flex-shrink-0"
          onMouseEnter={() => setShowVolumeSlider(true)}
          onMouseLeave={() => setShowVolumeSlider(false)}
        >
          <button
            onClick={handleMuteToggle}
            className="p-1 sm:p-1.5 hover:bg-white/10 rounded-full transition-colors"
          >
            {muted ? (
              <VolumeX className="w-4 h-4 text-white/70" />
            ) : (
              <Volume2 className="w-4 h-4 text-white/70" />
            )}
          </button>

          <AnimatePresence>
            {showVolumeSlider && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-black/80 backdrop-blur-md rounded-lg px-3 py-2 shadow-lg border border-white/10"
              >
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={muted ? 0 : Math.round(volume * 100)}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) / 100;
                    setVolume(val);
                    if (muted) setMuted(false);
                  }}
                  className="w-20 h-1 accent-green-400 cursor-pointer"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* End / Stop — red circle badge at the end */}
        <button
          onClick={handleStop}
          className="w-7 h-7 flex items-center justify-center bg-red-500 hover:bg-red-600 rounded-full transition-colors flex-shrink-0 ml-1"
          title="End Audio"
        >
          <X className="w-3.5 h-3.5 text-white" strokeWidth={3} />
        </button>
      </div>
    </motion.div>
  );
}
